/* eslint-disable @typescript-eslint/indent */
import React, { useContext, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import HotelDetails from '@customTypes/hotel-details';
import { ProductDetails as ProductDetailsData } from '@customTypes/product-details';
import SearchOption from '@customTypes/search-option';
import actionTypes from '@context/actionTypes';
import { store } from '@context/store';
import { dlpSmartCalendarOpen } from '@events/DLP';
import {
  getSmartCalendarAvailability,
  getSmartCalendarTimeGroupAvailability,
} from '@utils/services';
import Product from '@customTypes/product';
import BottomDrawerVariant from '../../BottomDrawer/BottomDrawerVariant';
import ButtonSecondary from '../../ButtonSecondary';
import Spinner from '../Spinner';
import SmartCalendarMobileFooter from './SmartCalendarMobileFooter';

interface DateModalProps {
  hotel: HotelDetails;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirmClick: (date: Date) => void;
  previouslySelectedDate: Date | null | undefined;
  productDetails: ProductDetailsData | undefined;
}

export default function SmartCalendarMobile({
  hotel,
  isModalOpen,
  setIsModalOpen,
  onConfirmClick,
  previouslySelectedDate,
  productDetails,
}: DateModalProps) {
  const [date, setDate] = useState<Date | null>(null);
  const [highlightDates, setHighlightDates] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [numberOfMonthsShown, setNumberOfMonthsShown] = useState<number>(2);
  const [selectedProductType, setSelectedProductType] = useState<SearchOption | null>(null);
  const [unavailableDates, setUnavailableDates] = useState<Date[]>([]);

  const datePickerMobileRef = useRef<any>();

  const globalState = useContext(store);
  const { dispatch } = globalState;

  const isMobileDatePicker = (): boolean => datePickerMobileRef && datePickerMobileRef.current;

  const getInventory = async (selectedOption: SearchOption, monthsShown?: number) => {
    setIsLoading(true);

    const limitedQuantityDatesArray: Date[] = [];
    const unavailableDatesArray: Date[] = [];
    const from = dayjs().format('YYYY-MM-DD');
    const to = dayjs()
      .add((monthsShown ?? numberOfMonthsShown) - 1, 'month')
      .endOf('month')
      .format('YYYY-MM-DD');

    try {
      // Extract the selected product from the products array.
      const selectedProduct = productDetails?.products.find((productData: Product) => {
        if (productData.is_product_time_group) {
          return productData.product_time_group_id === selectedOption.value;
        }

        return productData.product_id === +selectedOption.value;
      });

      const availability = selectedProduct?.is_product_time_group
        ? await getSmartCalendarTimeGroupAvailability(
            hotel.metadata.hotel_id,
            selectedOption.value,
            from,
            to,
          )
        : await getSmartCalendarAvailability(
            hotel.metadata.hotel_id,
            selectedOption.value,
            from,
            to,
          );

      if (availability) {
        availability.product_inventory?.forEach((item: { date: string; quantity: number }) => {
          if (item.quantity === 0) {
            unavailableDatesArray.push(new Date(`${item.date}T15:00:00.000Z`));
          } else if (item.quantity > 0 && item.quantity < 2) {
            limitedQuantityDatesArray.push(new Date(`${item.date}T15:00:00.000Z`));
          }
        });

        setUnavailableDates(unavailableDatesArray);
      }
    } catch (error) {
      // no error handling
    } finally {
      setIsLoading(false);
    }
  };

  const onCalendarClose = () => {
    // Reset:
    if (numberOfMonthsShown > 1) {
      const monthsShown = numberOfMonthsShown - 2;
      setNumberOfMonthsShown(monthsShown);
    }
    setSelectedProductType(null);
    setHighlightDates([]);
    dispatch({
      type: actionTypes.SET_SMART_CALENDAR_PRODUCT_FILTER,
      payload: null,
    });

    // Close
    dispatch({ type: actionTypes.OPEN_DATE_PICKER, payload: false });
    setIsModalOpen(false);
  };

  const handleConfirmClick = () => {
    if (date) {
      onConfirmClick(date);
    }

    onCalendarClose();
  };

  const scrollToSelectedDate = () => {
    document
      .getElementsByClassName('react-datepicker__day--selected')[0]
      ?.scrollIntoView({ block: 'center' });
  };

  const handleShowMoreClick = () => {
    // Needed to keep first month as current month on calendar re-render
    datePickerMobileRef.current.setPreSelection(new Date());

    if (numberOfMonthsShown < 7) {
      const monthsShown =
        numberOfMonthsShown < 6 ? numberOfMonthsShown + 2 : numberOfMonthsShown + 1;

      setNumberOfMonthsShown(monthsShown);

      if (selectedProductType) {
        getInventory(selectedProductType, monthsShown);
      }
    }
  };

  useEffect(() => {
    if (previouslySelectedDate) {
      setDate(previouslySelectedDate);

      // Calculate how many months to show to include the selected date
      const currentDate = new Date();
      const monthsDifference = Math.abs(dayjs(previouslySelectedDate).diff(currentDate, 'month'));

      // We need to show at least the months from current to selected date
      // Adding 1 because we want to include both the current month and the selected month
      const requiredMonthsToShow = Math.max(2, monthsDifference + 1);

      // Cap at 7 months maximum
      const monthsToShow = Math.min(7, requiredMonthsToShow);

      setNumberOfMonthsShown(monthsToShow);
    }
  }, [previouslySelectedDate]);

  // Ensure calendar opens to current month when numberOfMonthsShown changes
  useEffect(() => {
    if (datePickerMobileRef.current && isModalOpen) {
      setTimeout(() => {
        datePickerMobileRef.current.setPreSelection(new Date());
      }, 100);
    }
  }, [numberOfMonthsShown, isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      // Focus on the date picker input to ensure it receives keyboard events
      const datePickerInput = document.getElementById('date-picker-input');

      if (datePickerInput) {
        datePickerInput.focus();
      }

      // Needed to keep first month as current month on calendar re-render
      datePickerMobileRef.current.setPreSelection(new Date());

      setTimeout(() => {
        scrollToSelectedDate();
      }, 500);

      // Event
      dlpSmartCalendarOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  // Needed to always open the calendar on current month
  useEffect(() => {
    if (isMobileDatePicker()) {
      datePickerMobileRef.current.setPreSelection(new Date());
    }
  }, [isModalOpen, datePickerMobileRef]);

  useEffect(() => {
    setHighlightDates([{ 'react-datepicker__day--disabled': unavailableDates }]);
  }, [setHighlightDates, unavailableDates]);

  return (
    <BottomDrawerVariant title="Calendar" isOpen={isModalOpen} setIsOpen={onCalendarClose}>
      <div className="mobile-variant-date-picker px-4">
        <DatePicker
          dateFormat="MMMM dd, yyyy"
          disabledKeyboardNavigation
          focusSelectedMonth={false}
          highlightDates={highlightDates}
          id="date-picker-input"
          inline
          maxDate={dayjs().add(6, 'month').toDate()}
          minDate={new Date()}
          monthsShown={numberOfMonthsShown}
          onChange={(d) => {
            setDate(d);
          }}
          openToDate={new Date()}
          ref={datePickerMobileRef}
          selected={date}
          shouldCloseOnSelect={false}
          startDate={new Date()}
        />
      </div>

      {isLoading && <Spinner />}

      <div className="px-5 mb-200px w-full">
        {numberOfMonthsShown === 7 ? (
          <p className="text-center font-rp-pn-regular font-semibold text-rp-disabled text-sm">
            Unfortunately, {hotel.name} doesn&apos;t take bookings any further in advance
          </p>
        ) : (
          <ButtonSecondary classes="text-base" onClick={handleShowMoreClick}>
            Show More
          </ButtonSecondary>
        )}
      </div>

      <SmartCalendarMobileFooter
        date={date}
        handleConfirmClick={handleConfirmClick}
        hotel={hotel}
        onProductTypeChange={getInventory}
        productDetails={productDetails}
        selectedProductType={selectedProductType}
        setHighlightDates={setHighlightDates}
        setSelectedProductType={setSelectedProductType}
      />
    </BottomDrawerVariant>
  );
}
